import React, { useState, useEffect } from "react";
import {
	Button,
	List,
	ListItem,
	ListItemText,
	ListItemIcon,
	Switch,
	Typography,
	Box,
	Paper,
	FormControlLabel,
	useTheme,
	alpha,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import DesignSystemDialog from "../DesignSystemDialog";
import SettingsIcon from "@mui/icons-material/Settings";
import RestoreIcon from "@mui/icons-material/Restore";
import SaveIcon from "@mui/icons-material/Save";
import MenuIcon from "@mui/icons-material/Menu";

/**
 * Modal for configuring which sidebar menu items are visible
 */
const SidebarSettingsDialog = ({ 
	open, 
	onClose, 
	mainMenuItems = [], 
	bottomMenuItems = [],
	onSave,
	currentSettings = {}
}) => {
	const { t } = useTranslation();
	const theme = useTheme();

	// Create a local copy of settings to apply only when saved
	const [localSettings, setLocalSettings] = useState({});

	// Update local settings when the modal is opened or currentSettings changes
	useEffect(() => {
		if (open) {
			setLocalSettings({ ...currentSettings });
		}
	}, [open, currentSettings]);

	const handleToggle = (path) => {
		setLocalSettings((prev) => ({
			...prev,
			[path]: !prev[path],
		}));
	};

	const handleReset = () => {
		// Reset to all items visible (default)
		const defaultSettings = {};
		const allItems = [...mainMenuItems, ...bottomMenuItems];
		allItems.forEach(item => {
			defaultSettings[item.path] = true;
		});
		setLocalSettings(defaultSettings);
		onSave(defaultSettings);
		onClose();
	};

	const handleSave = () => {
		onSave(localSettings);
		onClose();
	};

	const handleCancel = () => {
		setLocalSettings({ ...currentSettings });
		onClose();
	};

	// Combine all items for display
	const allMenuItems = [
		...mainMenuItems.map(item => ({ ...item, section: 'main' })),
		...bottomMenuItems.map(item => ({ ...item, section: 'bottom' }))
	];

	// Group items by section
	const mainSectionItems = allMenuItems.filter(item => item.section === 'main');
	const bottomSectionItems = allMenuItems.filter(item => item.section === 'bottom');

	return (
		<DesignSystemDialog
			open={open}
			onClose={handleCancel}
			maxWidth="md"
			title={t("sidebar-settings") || "Sidebar Settings"}
			icon={<SettingsIcon />}
			actions={
				<>
					<Button 
						onClick={handleReset} 
						startIcon={<RestoreIcon />}
						variant="outlined"
						sx={{ 
							borderRadius: "12px",
							textTransform: "none",
							fontWeight: 600,
							transition: "all 0.2s ease",
						}}
					>
						{t("reset-to-defaults")}
					</Button>
					<Box sx={{ display: "flex", gap: 1 }}>
						<Button 
							onClick={handleCancel}
							variant="outlined"
							sx={{ 
								borderRadius: "12px",
								textTransform: "none",
								fontWeight: 600,
								transition: "all 0.2s ease",
							}}
						>
							{t("cancel")}
						</Button>
						<Button
							onClick={handleSave}
							variant="contained"
							color="primary"
							startIcon={<SaveIcon />}
							sx={{ 
								borderRadius: "12px",
								textTransform: "none",
								fontWeight: 600,
								transition: "all 0.2s ease",
							}}
						>
							{t("save")}
						</Button>
					</Box>
				</>
			}
			actionsProps={{
				sx: {
					justifyContent: "space-between",
				}
			}}
		>
			<Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
				<Typography variant="body1" color="textSecondary">
					{t("sidebar-settings-description") || "Customize which menu items are visible in the sidebar. Toggle items on or off to personalize your navigation."}
				</Typography>

				{/* Main Navigation Section */}
				{mainSectionItems.length > 0 && (
					<Box>
						<Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
							<MenuIcon sx={{ mr: 1.5, color: theme.palette.primary.main }} />
							<Typography variant="h6" color="primary" fontWeight={500}>
								{t("main-navigation") || "Main Navigation"}
							</Typography>
						</Box>
						<Paper 
							elevation={0} 
							sx={{ 
								p: 1.5, 
								borderRadius: theme.shape.borderRadius,
								bgcolor: alpha(theme.palette.primary.main, 0.05),
								border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`
							}}
						>
							<List sx={{ width: "100%" }} disablePadding>
								{mainSectionItems.map((item) => (
									<ListItem
										key={item.path}
										dense
										sx={{
											py: 2,
											px: 1.5,
											borderRadius: theme.shape.borderRadius,
											mb: 0.75,
											"&:last-child": { mb: 0 },
											"&:hover": {
												bgcolor: alpha(theme.palette.primary.main, 0.08),
											},
										}}
									>
										<ListItemIcon sx={{ minWidth: 48, color: "var(--primary-main)" }}>
											{item.icon}
										</ListItemIcon>
										<ListItemText
											id={`switch-list-label-${item.path}`}
											primary={
												<Typography variant="body1" fontWeight={500}>
													{item.label}
												</Typography>
											}
											secondary={item.description}
											sx={{ cursor: "pointer" }}
											onClick={() => handleToggle(item.path)}
										/>
										<FormControlLabel
											control={
												<Switch
													edge="end"
													checked={localSettings[item.path] !== false}
													onChange={() => handleToggle(item.path)}
													color="primary"
													inputProps={{
														"aria-labelledby": `switch-list-label-${item.path}`,
													}}
												/>
											}
											label=""
										/>
									</ListItem>
								))}
							</List>
						</Paper>
					</Box>
				)}

				{/* Bottom Navigation Section */}
				{bottomSectionItems.length > 0 && (
					<Box>
						<Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
							<MenuIcon sx={{ mr: 1.5, color: theme.palette.primary.main }} />
							<Typography variant="h6" color="primary" fontWeight={500}>
								{t("secondary-navigation") || "Secondary Navigation"}
							</Typography>
						</Box>
						<Paper 
							elevation={0} 
							sx={{ 
								p: 1.5, 
								borderRadius: theme.shape.borderRadius,
								bgcolor: alpha(theme.palette.primary.main, 0.05),
								border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`
							}}
						>
							<List sx={{ width: "100%" }} disablePadding>
								{bottomSectionItems.map((item) => (
									<ListItem
										key={item.path}
										dense
										sx={{
											py: 2,
											px: 1.5,
											borderRadius: theme.shape.borderRadius,
											mb: 0.75,
											"&:last-child": { mb: 0 },
											"&:hover": {
												bgcolor: alpha(theme.palette.primary.main, 0.08),
											},
										}}
									>
										<ListItemIcon sx={{ minWidth: 48, color: "var(--primary-main)" }}>
											{item.icon}
										</ListItemIcon>
										<ListItemText
											id={`switch-list-label-${item.path}`}
											primary={
												<Typography variant="body1" fontWeight={500}>
													{item.label}
												</Typography>
											}
											secondary={item.description}
											sx={{ cursor: "pointer" }}
											onClick={() => handleToggle(item.path)}
										/>
										<FormControlLabel
											control={
												<Switch
													edge="end"
													checked={localSettings[item.path] !== false}
													onChange={() => handleToggle(item.path)}
													color="primary"
													inputProps={{
														"aria-labelledby": `switch-list-label-${item.path}`,
													}}
												/>
											}
											label=""
										/>
									</ListItem>
								))}
							</List>
						</Paper>
					</Box>
				)}
			</Box>
		</DesignSystemDialog>
	);
};

export default SidebarSettingsDialog;
